/**
 * Dev server state for the preview panel.
 *
 * Two sources feed this store and they arrive out of band from each other:
 *
 * - `load` pulls the full picture (detections, resolved command, hostname) over
 *   RPC when the panel opens.
 * - `applyStatusChange` receives runtime transitions pushed on
 *   `orchestration.devServerStatusChanged` — booting, running, crashed.
 *
 * The push channel carries only the status, so it is merged into whatever
 * entry already exists rather than replacing it; otherwise a "running" push
 * would wipe the framework list the panel is rendering.
 */
import type {
  DevServerFrameworkId,
  DevServerStatus,
  ThreadDevServerInfo,
  ThreadId,
} from "@taskin-code/contracts";
import { DEFAULT_DEV_SERVER_STATUS } from "@taskin-code/contracts";
import { create } from "zustand";

import { readNativeApi } from "./nativeApi";

export type DevServerEntryStatus = "idle" | "loading" | "loaded" | "error";

export interface DevServerEntry {
  status: DevServerEntryStatus;
  info: ThreadDevServerInfo | null;
  /** Error from the last load/start/stop, shown inline in the panel. */
  error: string | null;
  /** True while a start or stop request is in flight. */
  pending: boolean;
}

const INITIAL_ENTRY: DevServerEntry = {
  status: "idle",
  info: null,
  error: null,
  pending: false,
};

interface DevServerStoreState {
  byThreadId: Record<ThreadId, DevServerEntry>;
  load: (threadId: ThreadId) => Promise<void>;
  start: (input: {
    threadId: ThreadId;
    frameworkId?: DevServerFrameworkId | null;
  }) => Promise<void>;
  stop: (threadId: ThreadId) => Promise<void>;
  setCommand: (threadId: ThreadId, command: string | null) => Promise<void>;
  /** Publishes or unpublishes the preview. The window is the server's to set. */
  setVisibility: (threadId: ThreadId, isPublic: boolean) => Promise<void>;
  applyStatusChange: (threadId: ThreadId, status: DevServerStatus) => void;
}

const toMessage = (cause: unknown): string =>
  cause instanceof Error ? cause.message : String(cause);

/**
 * The native API is absent until the transport connects. Throwing here funnels
 * that into the same error slot as a failed request, so the panel shows
 * "not connected" instead of silently doing nothing.
 */
const requireApi = () => {
  const api = readNativeApi();
  if (api === undefined) throw new Error("Not connected to the server");
  return api.orchestration;
};

export const useDevServerStore = create<DevServerStoreState>((set, get) => {
  const patch = (threadId: ThreadId, changes: Partial<DevServerEntry>): void => {
    set((state) => ({
      byThreadId: {
        ...state.byThreadId,
        [threadId]: { ...(state.byThreadId[threadId] ?? INITIAL_ENTRY), ...changes },
      },
    }));
  };

  return {
    byThreadId: {},

    load: async (threadId) => {
      patch(threadId, { status: "loading", error: null });
      try {
        const info = await requireApi().getThreadDevServer({ threadId });
        patch(threadId, { status: "loaded", info, error: null });
      } catch (cause) {
        patch(threadId, { status: "error", error: toMessage(cause) });
      }
    },

    start: async ({ threadId, frameworkId }) => {
      patch(threadId, { pending: true, error: null });
      try {
        const status = await requireApi().startThreadDevServer({
          threadId,
          ...(frameworkId != null ? { frameworkId } : {}),
        });
        get().applyStatusChange(threadId, status);
        // Re-read so the hostname and resolved command reflect what actually
        // got allocated, which the status alone does not carry.
        await get().load(threadId);
      } catch (cause) {
        patch(threadId, { error: toMessage(cause) });
      } finally {
        patch(threadId, { pending: false });
      }
    },

    stop: async (threadId) => {
      patch(threadId, { pending: true, error: null });
      try {
        const status = await requireApi().stopThreadDevServer({ threadId });
        get().applyStatusChange(threadId, status);
      } catch (cause) {
        patch(threadId, { error: toMessage(cause) });
      } finally {
        patch(threadId, { pending: false });
      }
    },

    setCommand: async (threadId, command) => {
      const trimmed = command === null ? null : command.trim();
      const devCommand = trimmed === "" ? null : trimmed;
      try {
        await requireApi().updateThreadDevCommand({ threadId, devCommand });
        await get().load(threadId);
      } catch (cause) {
        patch(threadId, { error: toMessage(cause) });
      }
    },

    setVisibility: async (threadId, isPublic) => {
      patch(threadId, { pending: true, error: null });
      try {
        const info = await requireApi().setThreadDevServerVisibility({ threadId, isPublic });
        patch(threadId, { status: "loaded", info, error: null });
      } catch (cause) {
        patch(threadId, { error: toMessage(cause) });
      } finally {
        patch(threadId, { pending: false });
      }
    },

    applyStatusChange: (threadId, status) => {
      const existing = get().byThreadId[threadId];
      if (existing?.info == null) {
        // A push can arrive before the panel has ever loaded — keep the status
        // so opening the panel shows the right state immediately.
        patch(threadId, {
          info: {
            threadId,
            status,
            detections: [],
            commandOverride: null,
            resolvedCommand: null,
            hostname: status.hostname ?? null,
            allocatedPort: status.internalPort ?? null,
            publicUntil: status.publicUntil ?? null,
          },
        });
        return;
      }
      // `publicUntil` is mirrored onto the info as well: the push is the fresher
      // of the two, and it is what expires the "Public" badge without a reload.
      patch(threadId, {
        info: { ...existing.info, status, publicUntil: status.publicUntil ?? null },
      });
    },
  };
});

export const selectDevServerEntry = (
  state: DevServerStoreState,
  threadId: ThreadId | null,
): DevServerEntry =>
  threadId === null ? INITIAL_ENTRY : (state.byThreadId[threadId] ?? INITIAL_ENTRY);

export const DEFAULT_STATUS = DEFAULT_DEV_SERVER_STATUS;
